import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Loader2, Play, X, RefreshCw, Clock, Film, ChevronDown, Check, Plus, Video } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Build a playable URL for /api/recipes/storage/... by attaching the auth token
// as a query param (HTML <video>/<img> tags cannot send Authorization headers).
function withAuthToken(url, token) {
  if (!url || !token) return url;
  if (url.startsWith('http')) return url;
  const fullUrl = `${API}${url}`;
  if (!url.startsWith('/api/recipes/storage/') && !url.startsWith('/api/storage/')) return fullUrl;
  const sep = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${sep}token=${encodeURIComponent(token)}`;
}

async function pollRecipeRun(runId, token) {
  const maxPolls = 600;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    try {
      const { data } = await axios.get(`${API}/api/recipes/kling-i2v/runs/${runId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.status === 'completed') return data;
      if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
    } catch (err) {
      if (err.response?.status === 404) continue;
      if (err.message && !err.response) throw err;
    }
  }
  throw new Error('Generation timed out');
}

export default function KlingDirectInputs({ token, onVideoStateChange, restoredData }) {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageOrientation, setImageOrientation] = useState('landscape');

  // Last frame
  const [lastFrameFile, setLastFrameFile] = useState(null);
  const [lastFramePreview, setLastFramePreview] = useState(null);

  // Motion ref video
  const [motionVideoFile, setMotionVideoFile] = useState(null);
  const [motionVideoName, setMotionVideoName] = useState('');
  const [motionVideoPreview, setMotionVideoPreview] = useState(null);

  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('720p');
  const [negativePrompt, setNegativePrompt] = useState('');

  const [generating, setGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsOpen, setRunsOpen] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fileInputRef = useRef(null);
  const lastFrameRef = useRef(null);
  const motionVideoRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/recipes/kling-i2v/runs`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => setRuns(data.runs || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!restoredData) return;
    if (restoredData.prompt) setPrompt(restoredData.prompt);
    if (restoredData.aspect_ratio) setAspectRatio(restoredData.aspect_ratio);
    if (restoredData.duration) setDuration(restoredData.duration);
    if (restoredData.resolution) setResolution(restoredData.resolution);
    if (restoredData.image_url) {
      setImageUrl(restoredData.image_url);
      setImagePreview(restoredData.image_url);
      const img = new Image();
      img.onload = () => setImageOrientation(img.height > img.width ? 'portrait' : 'landscape');
      img.src = restoredData.image_url;
    }
    if (restoredData.video_url) {
      const restoredVUrl = withAuthToken(restoredData.video_url, token);
      setVideoUrl(restoredVUrl);
      onVideoStateChange?.(false, restoredVUrl, null, null);
    }
  }, [restoredData]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const objUrl = URL.createObjectURL(file);
    setImagePreview(objUrl);
    setImageUrl(null);
    const img = new Image();
    img.onload = () => {
      const isPortrait = img.height > img.width;
      setImageOrientation(isPortrait ? 'portrait' : 'landscape');
      setAspectRatio(isPortrait ? '9:16' : '16:9');
    };
    img.src = objUrl;
  };

  const handleGenerate = async () => {
    if (!imageFile && !imageUrl) return alert('Please upload a keyframe image');
    if (!prompt.trim() && !motionVideoFile) return alert('Please enter a motion prompt or upload a motion capture video');
    try {
      setGenerating(true);
      setVideoUrl(null);
      try { onVideoStateChange?.(true, null, null, null); } catch {}

      // Build form data for the recipe runner
      const fd = new FormData();
      if (imageFile) fd.append('image', imageFile);
      else if (imageUrl) fd.append('image_url', imageUrl);
      fd.append('text_input', prompt.trim());
      // Combined dropdown: duration + aspect ratio
      fd.append('dropdown_value', `${duration}s — ${aspectRatio} ${aspectRatio === '16:9' ? 'Landscape' : aspectRatio === '9:16' ? 'Portrait' : 'Square'}`);
      // Motion capture: set style to trigger motion capture path in backend
      if (motionVideoFile) {
        fd.append('text_input_2', 'Motion Capture');
        fd.append('image2', motionVideoFile);  // Backend reads this as image2_bytes for motion ref video
      }
      if (lastFrameFile) fd.append('last_frame', lastFrameFile);
      if (negativePrompt.trim()) fd.append('negative_prompt', negativePrompt.trim());
      fd.append('resolution', resolution);

      const { data } = await axios.post(`${API}/api/recipes/kling-i2v/run`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });

      if (!data.run_id) throw new Error('No run_id returned');
      try { onVideoStateChange?.(true, null, imagePreview || imageUrl, prompt.trim(), lastFramePreview || null); } catch {}

      // Poll for completion
      const result = await pollRecipeRun(data.run_id, token);
      const videoOut = result.outputs?.find(o => o.type === 'video');
      if (videoOut?.url) {
        const vUrl = withAuthToken(videoOut.url, token);
        setVideoUrl(vUrl);
        try { onVideoStateChange?.(false, vUrl, null, null); } catch {}
        // Refresh runs
        axios.get(`${API}/api/recipes/kling-i2v/runs`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(({ data: d }) => setRuns(d.runs || [])).catch(() => {});
      }
    } catch (err) {
      alert(err.message || 'Generation failed');
      try { onVideoStateChange?.(false, null, null, null); } catch {}
    } finally {
      setGenerating(false);
    }
  };

  const loadRun = (run) => {
    if (run.inputs) {
      setPrompt(run.inputs.text_input || '');
      setDuration(run.inputs.duration || 5);
      setAspectRatio(run.inputs.aspect_ratio || '16:9');
      setResolution(run.inputs.resolution || '1080p');
    }
    const videoOut = run.outputs?.find(o => o.type === 'video');
    if (videoOut?.url) {
      const vUrl = withAuthToken(videoOut.url, token);
      setVideoUrl(vUrl);
      setGenerating(false);
      onVideoStateChange?.(false, vUrl, null, null);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-4" data-testid="kling-direct-inputs">
      {/* First Frame (Keyframe) */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">First Frame (Keyframe)</label>
        <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} />
        {imagePreview ? (
          <div className={`relative group rounded-xl overflow-hidden ${imageOrientation === 'portrait' ? 'max-w-[60%] mx-auto' : 'w-full'}`} style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
            <img src={imagePreview} alt="First Frame" className="w-full object-contain" style={{ maxHeight: imageOrientation === 'portrait' ? '280px' : '140px' }} />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm">Change</button>
            </div>
          </div>
        ) : (
          <button onClick={() => fileInputRef.current?.click()} className="w-full py-8 rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:bg-white/[0.04]" style={{ border: '1px dashed rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }} data-testid="upload-first-frame">
            <Upload className="w-5 h-5 text-white/30" />
            <span className="text-[11px] text-white/40">Upload first frame</span>
          </button>
        )}
      </div>

      {/* Last Frame (optional) */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Last Frame (Optional)</label>
        <input type="file" ref={lastFrameRef} accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f) { setLastFrameFile(f); setLastFramePreview(URL.createObjectURL(f)); } }} />
        {lastFramePreview ? (
          <div className={`relative group rounded-xl overflow-hidden ${imageOrientation === 'portrait' ? 'max-w-[60%] mx-auto' : 'w-full'}`} style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
            <img src={lastFramePreview} alt="Last Frame" className="w-full object-contain" style={{ maxHeight: imageOrientation === 'portrait' ? '280px' : '140px' }} />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button onClick={() => { setLastFrameFile(null); setLastFramePreview(null); }} className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm">Remove</button>
            </div>
          </div>
        ) : (
          <button onClick={() => lastFrameRef.current?.click()} className="w-full py-8 rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:bg-white/[0.04]" style={{ border: '1px dashed rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }} data-testid="upload-last-frame">
            <Plus className="w-5 h-5 text-white/25" />
            <span className="text-[11px] text-white/40">Add end frame for controlled transition</span>
          </button>
        )}
      </div>

      {/* Motion Capture Video */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Motion Capture — Reference Video (Optional)</label>
        <input type="file" ref={motionVideoRef} accept="video/*,.mp4,.mov,.webm" className="hidden" onChange={(e) => {
          const f = e.target.files[0];
          if (!f) return;
          if (motionVideoPreview) { try { URL.revokeObjectURL(motionVideoPreview); } catch {} }
          const objUrl = URL.createObjectURL(f);
          setMotionVideoFile(f);
          setMotionVideoName(f.name);
          setMotionVideoPreview(objUrl);
        }} />
        {motionVideoFile ? (
          <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="motion-video-preview">
            <div className="relative bg-black">
              <video
                src={motionVideoPreview}
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[240px] object-contain bg-black"
                data-testid="motion-video-player"
              />
            </div>
            <div className="flex items-center gap-2 px-3 py-2">
              <Video className="w-4 h-4 text-rose-400 shrink-0" />
              <span className="text-xs text-white/60 flex-1 truncate">{motionVideoName}</span>
              <button
                onClick={() => {
                  if (motionVideoPreview) { try { URL.revokeObjectURL(motionVideoPreview); } catch {} }
                  setMotionVideoFile(null);
                  setMotionVideoName('');
                  setMotionVideoPreview(null);
                }}
                className="p-1 hover:bg-white/5 rounded"
                data-testid="motion-video-remove"
              ><X className="w-3 h-3 text-white/40" /></button>
            </div>
          </div>
        ) : (
          <button onClick={() => motionVideoRef.current?.click()} className="w-full py-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:bg-white/[0.04]" style={{ border: '1px dashed rgba(244,63,94,0.15)', background: 'rgba(244,63,94,0.02)' }} data-testid="upload-motion-video">
            <Video className="w-4 h-4 text-rose-400/40" />
            <span className="text-[10px] text-rose-300/40">Upload reference video for motion transfer</span>
          </button>
        )}
      </div>

      {/* Prompt */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Motion Prompt</label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe the motion and action..." rows={3} className="w-full px-3 py-2.5 rounded-xl text-xs text-white/80 placeholder-white/20 bg-white/[0.03] border border-white/[0.08] focus:outline-none focus:border-rose-500/30 resize-none transition-colors" data-testid="kling-prompt-input" />
      </div>

      {/* Duration & Aspect Ratio */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Duration: {duration}s</label>
          <input type="range" min={3} max={15} step={1} value={duration} onChange={e => setDuration(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, #F43F5E ${((duration - 3) / 12) * 100}%, rgba(255,255,255,0.08) ${((duration - 3) / 12) * 100}%)` }} data-testid="kling-duration-slider" />
          <div className="flex justify-between text-[8px] text-white/20 mt-0.5"><span>3s</span><span>15s</span></div>
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Aspect Ratio</label>
          <div className="flex gap-1.5">
            {['16:9', '9:16', '1:1'].map(ar => (
              <button key={ar} onClick={() => setAspectRatio(ar)} className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${aspectRatio === ar ? 'bg-rose-500/15 border-rose-500/40 text-rose-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`} style={{ border: '1px solid' }} data-testid={`kling-ar-${ar.replace(':', '-')}`}>
                {ar}
              </button>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Resolution</label>
          <div className="flex gap-1.5">
            {['720p', '1080p'].map(res => (
              <button key={res} onClick={() => setResolution(res)} className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${resolution === res ? 'bg-rose-500/15 border-rose-500/40 text-rose-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`} style={{ border: '1px solid' }} data-testid={`kling-res-${res}-btn`}>
                {res}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Advanced */}
      <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-[9px] text-white/25 hover:text-white/40">
        <ChevronDown className={`w-3 h-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} /> Advanced
      </button>
      {showAdvanced && (
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Negative Prompt</label>
          <input value={negativePrompt} onChange={e => setNegativePrompt(e.target.value)} placeholder="cartoon, anime, blurry, distorted..." className="w-full px-3 py-2 rounded-xl text-xs text-white/70 placeholder-white/15 bg-white/[0.03] border border-white/[0.08] focus:outline-none focus:border-rose-500/20" data-testid="kling-negative-prompt" />
        </div>
      )}

      {/* Generate */}
      <button onClick={handleGenerate} disabled={generating || (!imageFile && !imageUrl) || (!prompt.trim() && !motionVideoFile)} className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: generating ? 'rgba(244,63,94,0.12)' : 'linear-gradient(135deg, #F43F5E, #E11D48)', boxShadow: generating ? 'none' : '0 4px 16px rgba(244,63,94,0.25)' }} data-testid="kling-generate-btn">
        {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating {duration}s {resolution} Video...</> : <><Play className="w-4 h-4" /> Generate Video</>}
      </button>

      {/* Recent Runs */}
      {runs.length > 0 && (
        <div data-testid="kling-recent-runs">
          <button onClick={() => setRunsOpen(!runsOpen)} className="flex items-center justify-between w-full py-1.5 mb-1">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-white/30" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Recent Runs</span>
              <span className="text-[9px] text-white/20 bg-white/[0.05] px-1.5 py-0.5 rounded-full">{runs.length}</span>
            </div>
            <ChevronDown className={`w-3 h-3 text-white/20 transition-transform ${runsOpen ? 'rotate-180' : ''}`} />
          </button>
          {runsOpen && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {runs.map(run => {
                const videoOut = run.outputs?.find(o => o.type === 'video');
                return (
                  <button key={run.run_id} onClick={() => loadRun(run)} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-all bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04]" data-testid={`kling-run-${run.run_id}`}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-black/40">
                      <Film className="w-4 h-4 text-white/20 m-auto mt-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-white/60 truncate">{run.inputs?.text_input || 'No prompt'}</p>
                      <span className={`text-[9px] font-medium ${run.status === 'completed' ? 'text-emerald-400/70' : run.status === 'failed' ? 'text-red-400/70' : 'text-rose-400/70'}`}>{run.status}</span>
                      <span className="text-[9px] text-white/20 ml-2">{run.inputs?.resolution || '1080p'}</span>
                      <span className="text-[9px] text-white/15 ml-2">{timeAgo(run.started_at)}</span>
                    </div>
                    {videoOut && <Check className="w-3 h-3 text-rose-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
